import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatCurrency, formatDate, getErrorMessage, monthNames, toYMD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Loader2, Calendar, ChevronLeft, ChevronRight, Plus, Trash2, 
  AlertTriangle, GripVertical, CalendarDays, Clock, Settings, GanttChart,
  Download, FileImage, FileText, Wrench, ClipboardList, TrendingUp
} from "lucide-react";
import WorkTrackingTab from "../components/WorkTrackingTab";
import WorkloadView from "../components/WorkloadView";
import SavEditDialog from "@/components/calendar/SavEditDialog";

export const CalendarPage = () => {
  const [loading, setLoading] = useState(true);
  const [planningData, setPlanningData] = useState({ planning_items: [], non_working_dates: [] });
  const [nonWorkingDays, setNonWorkingDays] = useState([]);
  const [absencesData, setAbsencesData] = useState([]);
  const [savData, setSavData] = useState([]);
  const [workExtraData, setWorkExtraData] = useState([]);
  const [calendarColors, setCalendarColors] = useState({});
  const [showAbsences, setShowAbsences] = useState(true);
  const [showSav, setShowSav] = useState(true);
  const [showExtras, setShowExtras] = useState(true);
  const [viewMode, setViewMode] = useState("month"); // week, month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [constraintsDialogOpen, setConstraintsDialogOpen] = useState(false);
  const [nonWorkingDialogOpen, setNonWorkingDialogOpen] = useState(false);
  const [newNonWorkingDay, setNewNonWorkingDay] = useState({ date: "", name: "", recurring: false });
  const [draggedItem, setDraggedItem] = useState(null);
  const timelineRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // SAV edit dialog
  const [savDialogOpen, setSavDialogOpen] = useState(false);
  const [editingSav, setEditingSav] = useState(null);
  const [savForm, setSavForm] = useState({
    client_name: "", description: "", order_id: "", employee_ids: [],
    subcontractor_id: "", cost: "", duration_days: "1", operators: "1",
    intervention_date: "", status: "planifie", notes: ""
  });
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);

  const exportTimelineToPdf = async (asImage = false) => {
    if (!timelineRef.current) return;
    setExportingPdf(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(timelineRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: timelineRef.current.scrollWidth,
        height: timelineRef.current.scrollHeight,
        windowWidth: timelineRef.current.scrollWidth
      });
      const imgData = canvas.toDataURL("image/png");
      const filename = `timeline-${monthNames[currentDate.getMonth()]}-${currentDate.getFullYear()}`;
      if (asImage) {
        const link = document.createElement("a");
        link.download = `${filename}.png`;
        link.href = imgData;
        link.click();
        toast.success("Image téléchargée");
      } else {
        const { jsPDF } = await import("jspdf");
        // Landscape pour la timeline
        const pdfWidth = canvas.width > canvas.height * 1.4 ? 420 : 297;
        const pdfHeight = canvas.width > canvas.height * 1.4 ? 297 : 420;
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: [pdfWidth, pdfHeight]
        });
        const ratio = Math.min(pdfWidth / (canvas.width / 2), pdfHeight / (canvas.height / 2));
        const imgW = (canvas.width / 2) * ratio;
        const imgH = (canvas.height / 2) * ratio;
        pdf.addImage(imgData, "PNG", 5, 5, imgW, imgH);
        pdf.save(`${filename}.pdf`);
        toast.success("PDF téléchargé");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingPdf(false);
    }
  };
  
  // Constraints form
  const [constraints, setConstraints] = useState({
    start_date_constraint: "",
    end_date_constraint: "",
    allow_saturday: false,
    allow_sunday: false,
    parallel_with: []
  });

  // Get start and end dates for calendar view
  const getViewDates = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (viewMode === "week") {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay() + 1); // Monday
      const end = new Date(start);
      end.setDate(end.getDate() + 6); // Sunday
      return { start, end };
    } else {
      // Month view - show full month
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      return { start, end };
    }
  }, [currentDate, viewMode]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getViewDates();
      
      const [planningRes, nonWorkingRes, absencesRes, savRes, extrasRes, colorsRes] = await Promise.all([
        api.getPlanningCalendar({
          start_date: toYMD(start),
          end_date: toYMD(end)
        }),
        api.getNonWorkingDays({ year: currentDate.getFullYear() }),
        api.getAbsencesCalendar({
          start_date: toYMD(start),
          end_date: toYMD(end)
        }).catch(() => ({ data: [] })),
        api.getSAVCalendar({
          start_date: toYMD(start),
          end_date: toYMD(end)
        }).catch(() => ({ data: [] })),
        api.getWorkEntriesCalendar?.({
          start_date: toYMD(start),
          end_date: toYMD(end)
        }).catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
        api.getCalendarColors?.().catch(() => ({ data: { colors: {} } })) ?? Promise.resolve({ data: { colors: {} } }),
      ]);
      
      setPlanningData(planningRes.data);
      setNonWorkingDays(nonWorkingRes.data);
      setAbsencesData(absencesRes.data || []);
      setSavData(savRes.data || []);
      setWorkExtraData(extrasRes.data || []);
      setCalendarColors(colorsRes.data?.colors || {});
    } catch (error) {
      toast.error("Erreur lors du chargement");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getViewDates, currentDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch reference data for SAV dialog
  useEffect(() => {
    (async () => {
      try {
        const [empRes, ordRes, subRes] = await Promise.all([
          api.getEmployees(), api.getOrders(), api.getSubcontractors()
        ]);
        setEmployees(empRes.data);
        setOrders(ordRes.data);
        setSubcontractors(subRes.data);
      } catch {}
    })();
  }, []);

  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  const openConstraintsDialog = (order) => {
    setSelectedOrder(order);
    setConstraints({
      start_date_constraint: order.start_constraint || "",
      end_date_constraint: order.end_constraint || "",
      allow_saturday: order.allow_saturday || false,
      allow_sunday: order.allow_sunday || false,
      parallel_with: order.parallel_with || []
    });
    setConstraintsDialogOpen(true);
  };

  const handleSaveConstraints = async () => {
    try {
      await api.updateOrderPlanningConstraints(selectedOrder.order_id, {
        start_date_constraint: constraints.start_date_constraint || null,
        end_date_constraint: constraints.end_date_constraint || null,
        allow_saturday: constraints.allow_saturday,
        allow_sunday: constraints.allow_sunday,
        parallel_with: constraints.parallel_with
      });
      toast.success("Contraintes mises à jour");
      setConstraintsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddNonWorkingDay = async () => {
    try {
      await api.createNonWorkingDay(newNonWorkingDay);
      toast.success("Jour non travaillé ajouté");
      setNewNonWorkingDay({ date: "", name: "", recurring: false });
      setNonWorkingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteNonWorkingDay = async (dayId) => {
    try {
      await api.deleteNonWorkingDay(dayId);
      toast.success("Jour marqué comme travaillé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const STATUS_MAP = {
    planifie: "Planifié", en_cours: "En cours", termine: "Terminé", annule: "Annulé"
  };

  const openSavDialog = async (savItem) => {
    try {
      const res = await api.getSAV(savItem.id);
      const sav = res.data;
      setEditingSav(sav);
      setSavForm({
        client_name: sav.client_name || "",
        description: sav.description || "",
        order_id: sav.order_id || "",
        employee_ids: sav.employee_ids || [],
        subcontractor_id: sav.subcontractor_id || "",
        cost: sav.cost || "",
        duration_days: sav.duration_days || "1",
        operators: sav.operators || "1",
        intervention_date: sav.intervention_date || "",
        status: sav.status || "planifie",
        notes: sav.notes || ""
      });
      setSavDialogOpen(true);
    } catch {
      toast.error("Erreur chargement SAV");
    }
  };

  const handleSaveSav = async () => {
    if (!savForm.client_name.trim() || !savForm.description.trim()) {
      toast.error("Client et description obligatoires");
      return;
    }
    try {
      const payload = {
        ...savForm,
        cost: parseFloat(savForm.cost) || 0,
        duration_days: parseFloat(savForm.duration_days) || 1,
        operators: parseInt(savForm.operators) || 1,
        order_id: savForm.order_id || null,
        subcontractor_id: savForm.subcontractor_id || null,
        intervention_date: savForm.intervention_date || null
      };
      if (editingSav) {
        await api.updateSAV(editingSav.id, payload);
        toast.success("SAV modifié");
      } else {
        await api.createSAV(payload);
        toast.success("SAV créé");
      }
      setSavDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const handleDeleteSav = async () => {
    if (!editingSav) return;
    try {
      await api.deleteSAV(editingSav.id);
      toast.success("SAV supprimé");
      setSavDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const toggleSavEmployee = (empId) => {
    setSavForm(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(empId)
        ? prev.employee_ids.filter(id => id !== empId)
        : [...prev.employee_ids, empId]
    }));
  };

  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (!draggedItem) return;
    
    const items = [...planningData.planning_items];
    const [removed] = items.splice(draggedItem.index, 1);
    items.splice(targetIndex, 0, removed);
    
    // Update order IDs
    const orderIds = items.map(i => i.order_id);
    
    try {
      await api.reorderPlanning(orderIds);
      toast.success("Planning réorganisé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la réorganisation");
    }
    
    setDraggedItem(null);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const { start, end } = getViewDates();
    const days = [];
    const current = new Date(start);
    
    // For month view, start from the first Monday before or on the 1st
    if (viewMode === "month") {
      const firstDay = new Date(current);
      while (firstDay.getDay() !== 1) {
        firstDay.setDate(firstDay.getDate() - 1);
      }
      current.setTime(firstDay.getTime());
    }
    
    while (current <= end || days.length % 7 !== 0) {
      const dateStr = toYMD(current);
      const dayOfWeek = current.getDay(); // 0=Sunday, 6=Saturday
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;
      const isWeekend = isSaturday || isSunday;
      const isNonWorking = planningData.non_working_dates.includes(dateStr);
      const isCurrentMonth = current.getMonth() === currentDate.getMonth();
      const isToday = current.toDateString() === new Date().toDateString();
      
      // Find orders that span this day AND are actually working on this day
      const ordersOnDay = planningData.planning_items.filter(item => {
        // Compare using dateStr to avoid timezone issues
        const itemStartStr = item.start_date;
        const itemEndStr = item.end_date;
        
        // Check if the day is within the order's range (string comparison works for ISO dates)
        if (dateStr < itemStartStr || dateStr > itemEndStr) return false;
        
        // Check if this is a working day for this order
        // If it's a non-working day (holiday), skip
        if (isNonWorking) return false;
        
        // If it's Saturday and Saturday work is not allowed for THIS order, skip
        if (isSaturday && item.allow_saturday !== true) return false;
        
        // If it's Sunday and Sunday work is not allowed for THIS order, skip
        if (isSunday && item.allow_sunday !== true) return false;
        
        return true;
      });
      
      days.push({
        date: new Date(current),
        dateStr,
        isWeekend,
        isNonWorking,
        isCurrentMonth,
        isToday,
        orders: ordersOnDay,
        absences: absencesData.filter(a => dateStr >= a.start_date && dateStr <= a.end_date),
        savs: savData.filter(s => s.intervention_date === dateStr),
        extras: workExtraData.filter(e => e.date === dateStr),
        capacityInfo: (planningData.low_capacity_days || []).find(lc => lc.date === dateStr) || null
      });
      
      current.setDate(current.getDate() + 1);
      
      // Limit to 42 days for month view (6 weeks)
      if (viewMode === "month" && days.length >= 42) break;
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  const getOrderColor = (status, hasAlerts) => {
    if (hasAlerts) return "bg-red-100 border-red-300 text-red-800";
    if (status === "in_progress") return "bg-blue-100 border-blue-300 text-blue-800";
    return "bg-green-100 border-green-300 text-green-800";
  };

  const formatDateDisplay = () => {
    if (viewMode === "week") {
      const { start, end } = getViewDates();
      return `${start.getDate()} - ${end.getDate()} ${monthNames[end.getMonth()]} ${end.getFullYear()}`;
    }
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Calendrier & Planning</h1>
          <p className="text-muted-foreground mt-1">
            Planifiez et visualisez vos commandes
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={showAbsences ? "default" : "outline"}
            onClick={() => setShowAbsences(!showAbsences)}
            data-testid="toggle-absences"
          >
            🏖 {showAbsences ? "Masquer" : "Afficher"} absences
          </Button>
          <Button 
            variant={showSav ? "default" : "outline"}
            onClick={() => setShowSav(!showSav)}
            data-testid="toggle-sav"
          >
            <Wrench className="h-4 w-4 mr-1" />
            {showSav ? "Masquer" : "Afficher"} SAV
          </Button>
          <Button 
            variant={showExtras ? "default" : "outline"}
            onClick={() => setShowExtras(!showExtras)}
            data-testid="toggle-extras"
          >
            {showExtras ? "Masquer" : "Afficher"} Atelier/Fourn.
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setNonWorkingDialogOpen(true)}
            data-testid="manage-days-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Jours non travaillés
          </Button>
        </div>
      </div>

      <Tabs defaultValue="calendar" className="space-y-6">
        <TabsList>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Calendrier
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-2">
            <GanttChart className="h-4 w-4" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="list" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Liste du planning
          </TabsTrigger>
          <TabsTrigger value="tracking" className="flex items-center gap-2" data-testid="tab-tracking">
            <ClipboardList className="h-4 w-4" />
            Suivi des chantiers
          </TabsTrigger>
          <TabsTrigger value="workload" className="flex items-center gap-2" data-testid="tab-workload">
            <TrendingUp className="h-4 w-4" />
            Charge ouvriers
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Jours non travaillés
          </TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div className="flex items-center gap-4">
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Semaine</SelectItem>
                    <SelectItem value="month">Mois</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={navigatePrevious}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" onClick={navigateToday}>
                    Aujourd'hui
                  </Button>
                  <Button variant="outline" size="icon" onClick={navigateNext}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <h2 className="text-xl font-heading font-bold">{formatDateDisplay()}</h2>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="border rounded-sm overflow-hidden">
                  {/* Header row */}
                  <div className="grid grid-cols-7 bg-muted">
                    {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(day => (
                      <div key={day} className="p-2 text-center font-medium text-sm border-r last:border-r-0">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar grid */}
                  <div className="grid grid-cols-7">
                    {calendarDays.map((day, idx) => (
                      <div
                        key={idx}
                        className={`min-h-24 p-1 border-r border-b last:border-r-0 ${
                          !day.isCurrentMonth ? "bg-muted/30" : ""
                        } ${day.isToday ? "bg-primary/5" : ""} ${
                          day.isNonWorking ? "bg-red-50" : ""
                        } ${day.isWeekend && !day.isNonWorking ? "bg-orange-50/50" : ""} ${
                          !day.isNonWorking && !day.isWeekend && day.capacityInfo ? "bg-amber-50" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm ${
                            day.isToday ? "bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center" : ""
                          } ${!day.isCurrentMonth ? "text-muted-foreground" : ""}`}>
                            {day.date.getDate()}
                          </span>
                          {day.isNonWorking && (
                            <Badge variant="outline" className="text-xs bg-red-100">Férié</Badge>
                          )}
                          {!day.isNonWorking && !day.isWeekend && day.capacityInfo && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1 bg-amber-100 border-amber-400 text-amber-800"
                              title={`Capacité réduite : ${day.capacityInfo.available}/${day.capacityInfo.total} ouvriers`}
                              data-testid={`low-capacity-${day.dateStr}`}
                            >
                              {day.capacityInfo.available}/{day.capacityInfo.total}
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-1">
                          {day.orders.slice(0, 3).map((order, orderIdx) => {
                            const useTypeColor = !!order.color && !order.subcontractor_id && !(order.alerts?.length);
                            const txt = order.text_color === "black" ? "#0f172a"
                              : order.text_color === "gray" ? "#475569"
                              : "#ffffff";
                            return (
                              <div
                                key={orderIdx}
                                className={`text-xs p-1 rounded truncate cursor-pointer border ${order.subcontractor_id ? "bg-purple-100 border-purple-300 text-purple-800" : useTypeColor ? "border-transparent" : getOrderColor(order.status, order.alerts?.length > 0)}`}
                                style={useTypeColor ? { backgroundColor: order.color, color: txt } : {}}
                                onClick={() => openConstraintsDialog(order)}
                                title={`${order.client_name}${order.city ? " — " + order.city : ""}${order.order_type_name ? " — " + order.order_type_name : ""}\nRéf : ${order.reference}${order.subcontractor_id ? "\n(Sous-traitant)" : ""}\n${order.start_date || ""} → ${order.end_date || ""}`}
                              >
                                {order.subcontractor_id && "🏗 "}{order.client_name || order.reference}
                                {order.alerts?.length > 0 && (
                                  <AlertTriangle className="h-3 w-3 inline ml-1 text-red-500" />
                                )}
                              </div>
                            );
                          })}
                          {day.orders.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{day.orders.length - 3} autres
                            </span>
                          )}
                          {showAbsences && day.absences && day.absences.length > 0 && (
                            <div className="pt-1 mt-1 border-t border-dashed border-muted">
                              {day.absences.slice(0, 2).map((abs, ai) => {
                                const absKey = `absence_${abs.absence_type}`;
                                const absColor = calendarColors[absKey];
                                const period = abs.is_half_day ? (abs.half_day_period === "afternoon" ? " (PM)" : abs.half_day_period === "morning" ? " (AM)" : " ½j") : "";
                                const timeInfo = (abs.start_time && abs.end_time) ? ` ${abs.start_time}-${abs.end_time}` : "";
                                return (
                                  <div
                                    key={ai}
                                    className="text-xs p-1 rounded truncate border"
                                    style={absColor ? { backgroundColor: absColor, color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fef3c7', borderColor: '#fde68a', color: '#78350f' }}
                                    title={`${abs.employee_name} — ${abs.absence_type}${period}${timeInfo}${abs.reason ? '\n' + abs.reason : ''}`}
                                  >
                                    🏖 {abs.employee_name.split(' ').map(w => w[0]).join('').toUpperCase()}{period} · {abs.absence_type}
                                  </div>
                                );
                              })}
                              {day.absences.length > 2 && (
                                <span className="text-xs text-amber-700">+{day.absences.length - 2} abs.</span>
                              )}
                            </div>
                          )}
                          {showSav && day.savs && day.savs.length > 0 && (
                            <div className="pt-1 mt-1 border-t border-dashed border-red-200">
                              {day.savs.slice(0, 2).map((sav, si) => (
                                <div
                                  key={si}
                                  className="text-xs p-1 rounded truncate border cursor-pointer hover:opacity-80"
                                  style={calendarColors.sav ? { backgroundColor: calendarColors.sav, color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fee2e2', borderColor: '#fca5a5', color: '#7f1d1d' }}
                                  title={`SAV: ${sav.client_name} - ${sav.description} (${sav.cost}€)${sav.employee_names?.length ? ' - ' + sav.employee_names.join(', ') : ''}`}
                                  onClick={() => openSavDialog(sav)}
                                >
                                  <Wrench className="h-3 w-3 inline mr-0.5" />
                                  {sav.client_name}
                                </div>
                              ))}
                              {day.savs.length > 2 && (
                                <span className="text-xs text-red-600">+{day.savs.length - 2} SAV</span>
                              )}
                            </div>
                          )}
                          {showExtras && day.extras && day.extras.length > 0 && (
                            <div className="pt-1 mt-1 border-t border-dashed border-slate-200">
                              {day.extras.slice(0, 2).map((ex, ei) => {
                                const key = ex.entry_type === "atelier"
                                  ? "atelier"
                                  : ex.entry_type === "fournisseur"
                                    ? "fournisseur"
                                    : null;
                                const color = key ? calendarColors[key] : null;
                                const label = ex.entry_type === "atelier"
                                  ? "Atelier"
                                  : ex.entry_type === "fournisseur"
                                    ? "Fournisseur"
                                    : (ex.custom_label || "Autre");
                                const initials = (ex.employee_name || "").split(" ").map(w => w[0]).filter(Boolean).join("").toUpperCase();
                                const timeInfo = ex.start_time && ex.end_time ? ` ${ex.start_time}-${ex.end_time}` : "";
                                return (
                                  <div
                                    key={ei}
                                    className="text-xs p-1 rounded truncate border"
                                    style={color
                                      ? { backgroundColor: color, color: "#fff", borderColor: "transparent" }
                                      : { backgroundColor: "#f1f5f9", borderColor: "#cbd5e1", color: "#0f172a" }}
                                    title={`${label} — ${ex.employee_name || ""}${timeInfo}${ex.comment ? "\n" + ex.comment : ""}`}
                                  >
                                    {label} · {initials || "—"}{timeInfo}
                                  </div>
                                );
                              })}
                              {day.extras.length > 2 && (
                                <span className="text-xs text-slate-600">+{day.extras.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline / Gantt View */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="font-heading">Vue Timeline</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Visualisez vos commandes sur une ligne de temps
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportTimelineToPdf(false)}
                  disabled={exportingPdf || planningData.planning_items.length === 0}
                  data-testid="export-timeline-pdf"
                >
                  {exportingPdf ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportTimelineToPdf(true)}
                  disabled={exportingPdf || planningData.planning_items.length === 0}
                  data-testid="export-timeline-image"
                >
                  {exportingPdf ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileImage className="h-4 w-4 mr-1" />}
                  Image
                </Button>
                <Button variant="outline" size="icon" onClick={navigatePrevious}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" onClick={navigateToday}>
                  Aujourd'hui
                </Button>
                <Button variant="outline" size="icon" onClick={navigateNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : planningData.planning_items.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune commande à afficher
                </p>
              ) : (
                <div className="overflow-x-auto" ref={timelineRef}>
                  {/* Timeline Header - Days */}
                  <div className="min-w-[1200px]">
                    {(() => {
                      // Generate timeline for current month
                      const year = currentDate.getFullYear();
                      const month = currentDate.getMonth();
                      const daysInMonth = new Date(year, month + 1, 0).getDate();
                      const days = Array.from({ length: daysInMonth }, (_, i) => {
                        const d = new Date(year, month, i + 1);
                        const ds = toYMD(d);
                        return {
                          date: d,
                          dateStr: ds,
                          dayNum: i + 1,
                          dayName: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][d.getDay()],
                          isWeekend: d.getDay() === 0 || d.getDay() === 6,
                          isNonWorking: planningData.non_working_dates.includes(ds),
                          isToday: d.toDateString() === new Date().toDateString()
                        };
                      });
                      
                      const dayWidth = 36; // pixels per day
                      const labelWidth = 200; // pixels for order labels
                      
                      return (
                        <>
                          {/* Month/Year header */}
                          <div className="flex items-center mb-2">
                            <div style={{ width: labelWidth }} className="shrink-0 font-heading font-bold text-lg">
                              {monthNames[month]} {year}
                            </div>
                          </div>
                          
                          {/* Days header */}
                          <div className="flex border-b">
                            <div style={{ width: labelWidth }} className="shrink-0 p-2 font-medium border-r bg-muted">
                              Commande
                            </div>
                            <div className="flex">
                              {days.map((day) => (
                                <div
                                  key={day.dayNum}
                                  style={{ width: dayWidth }}
                                  className={`text-center text-xs p-1 border-r ${
                                    day.isToday ? 'bg-primary text-primary-foreground' : 
                                    day.isNonWorking ? 'bg-red-100' :
                                    day.isWeekend ? 'bg-orange-50' : 'bg-muted'
                                  }`}
                                >
                                  <div className="font-medium">{day.dayNum}</div>
                                  <div className="text-[10px]">{day.dayName}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Orders rows */}
                          <div className="divide-y">
                            {planningData.planning_items.map((item) => {
                              const startDate = new Date(item.start_date);
                              const endDate = new Date(item.end_date);
                              
                              // Calculate position and width
                              const startDay = startDate.getMonth() === month ? startDate.getDate() : 1;
                              const endDay = endDate.getMonth() === month ? endDate.getDate() : daysInMonth;
                              const barStart = (startDay - 1) * dayWidth;
                              
                              // Calculate width based on WORKING days only
                              let workingDays = 0;
                              let totalSpan = endDay - startDay + 1;
                              for (let dd = startDay; dd <= endDay; dd++) {
                                const checkDate = new Date(year, month, dd);
                                const checkStr = toYMD(checkDate);
                                const dow = checkDate.getDay();
                                const isSat = dow === 6;
                                const isSun = dow === 0;
                                const isNW = planningData.non_working_dates.includes(checkStr);
                                if (!isNW && (!isSat || item.allow_saturday) && (!isSun || item.allow_sunday)) {
                                  workingDays++;
                                }
                              }
                              const barWidth = totalSpan * dayWidth;
                              
                              // Check if order is in this month
                              const isInMonth = (startDate.getMonth() === month && startDate.getFullYear() === year) ||
                                               (endDate.getMonth() === month && endDate.getFullYear() === year) ||
                                               (startDate < new Date(year, month, 1) && endDate > new Date(year, month + 1, 0));
                              
                              if (!isInMonth) return null;

                              // Build segments: solid for working days, striped for non-working
                              const segments = [];
                              for (let dd = startDay; dd <= endDay; dd++) {
                                const checkDate = new Date(year, month, dd);
                                const checkStr = toYMD(checkDate);
                                const dow = checkDate.getDay();
                                const isSat = dow === 6;
                                const isSun = dow === 0;
                                const isNW = planningData.non_working_dates.includes(checkStr);
                                const isWorking = !isNW && (!isSat || item.allow_saturday) && (!isSun || item.allow_sunday);
                                segments.push({ day: dd, isWorking });
                              }
                              
                              return (
                                <div key={item.order_id} className="flex items-center h-12 hover:bg-muted/30">
                                  <div 
                                    style={{ width: labelWidth }} 
                                    className="shrink-0 p-2 border-r truncate cursor-pointer hover:bg-muted"
                                    onClick={() => openConstraintsDialog(item)}
                                    title={`${item.client_name}${item.city ? " — " + item.city : ""}${item.order_type_name ? " — " + item.order_type_name : ""}\nRéf : ${item.reference}\nDébut : ${item.start_date}  Fin : ${item.end_date}`}
                                  >
                                    <div className="text-sm font-medium truncate">{item.client_name}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">{item.city || item.reference}</div>
                                  </div>
                                  <div className="relative flex-1 h-full" style={{ minWidth: daysInMonth * dayWidth }}>
                                    {/* Grid lines */}
                                    <div className="absolute inset-0 flex">
                                      {days.map((day) => (
                                        <div
                                          key={day.dayNum}
                                          style={{ width: dayWidth }}
                                          className={`h-full border-r ${
                                            day.isNonWorking ? 'bg-red-50' :
                                            day.isWeekend ? 'bg-orange-50/30' : ''
                                          }`}
                                        />
                                      ))}
                                    </div>
                                    {/* Order bar with segments */}
                                    {segments.map((seg, si) => {
                                      const segLeft = (seg.day - 1) * dayWidth;
                                      const useTypeColor = !!item.color && !item.alerts?.length && !item.subcontractor_id;
                                      const baseColor = item.alerts?.length > 0 
                                        ? 'bg-red-500 text-white' 
                                        : item.subcontractor_id 
                                        ? 'bg-purple-500 text-white'
                                        : item.color
                                        ? 'text-white'
                                        : item.status === 'in_progress'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-green-500 text-white';
                                      
                                      if (!seg.isWorking) {
                                        return (
                                          <div
                                            key={si}
                                            className="absolute top-2 h-8 opacity-20 bg-gray-400"
                                            style={{ left: segLeft, width: dayWidth }}
                                          />
                                        );
                                      }
                                      
                                      const isFirst = si === 0 || !segments[si - 1]?.isWorking;
                                      const isLast = si === segments.length - 1 || !segments[si + 1]?.isWorking;
                                      
                                      return (
                                        <div
                                          key={si}
                                          className={`absolute top-2 h-8 flex items-center text-xs font-medium cursor-pointer hover:opacity-80 ${baseColor} ${isFirst ? 'rounded-l-sm' : ''} ${isLast ? 'rounded-r-sm' : ''}`}
                                          style={{ left: segLeft, width: dayWidth, ...(useTypeColor ? { backgroundColor: item.color } : {}) }}
                                          onClick={() => openConstraintsDialog(item)}
                                          title={`${item.client_name}${item.city ? " — " + item.city : ""}${item.order_type_name ? " — " + item.order_type_name : ""}\nRéf : ${item.reference}\n${item.duration_days}j ouvrés — ${item.start_date} → ${item.end_date}`}
                                        >
                                          {isFirst && <span className="truncate px-1">{item.client_name}</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}

                            {/* SAV rows */}
                            {showSav && savData.filter(s => {
                              if (!s.intervention_date) return false;
                              const d = new Date(s.intervention_date);
                              return d.getMonth() === month && d.getFullYear() === year;
                            }).map((sav) => {
                              const intDate = new Date(sav.intervention_date);
                              const startDay = intDate.getDate();
                              const durationDays = Math.max(1, Math.ceil(sav.duration_days || 1));
                              const barStart = (startDay - 1) * dayWidth;
                              const barW = durationDays * dayWidth;

                              return (
                                <div key={`sav-${sav.id}`} className="flex items-center h-12 hover:bg-red-50/30">
                                  <div style={{ width: labelWidth }} className="shrink-0 p-2 border-r truncate">
                                    <div className="font-mono text-xs flex items-center gap-1 text-red-700">
                                      <Wrench className="h-3 w-3" /> SAV
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{sav.client_name}</div>
                                  </div>
                                  <div className="relative flex-1 h-full" style={{ minWidth: daysInMonth * dayWidth }}>
                                    <div className="absolute inset-0 flex">
                                      {days.map((day) => (
                                        <div key={day.dayNum} style={{ width: dayWidth }} className={`h-full border-r ${day.isNonWorking ? 'bg-red-50' : day.isWeekend ? 'bg-orange-50/30' : ''}`} />
                                      ))}
                                    </div>
                                    <div
                                      className="absolute top-2 h-8 rounded-sm flex items-center px-2 text-xs font-medium bg-red-500 text-white cursor-pointer hover:opacity-80"
                                      style={{ left: barStart, width: Math.max(barW, 30) }}
                                      onClick={() => openSavDialog(sav)}
                                      title={`SAV: ${sav.client_name} - ${sav.description} (${sav.cost}€, ${sav.duration_days}j, ${sav.operators}op)${sav.employee_names?.length ? '\nÉquipe: ' + sav.employee_names.join(', ') : ''}`}
                                    >
                                      <Wrench className="h-3 w-3 mr-1 shrink-0" />
                                      <span className="truncate">{sav.duration_days}j</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Legend */}
                          <div className="flex items-center flex-wrap gap-4 mt-4 pt-4 border-t text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-green-500 rounded-sm"></div>
                              <span>En attente</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-blue-500 rounded-sm"></div>
                              <span>En cours</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-purple-500 rounded-sm"></div>
                              <span>Sous-traitant</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-red-500 rounded-sm flex items-center justify-center"><Wrench className="h-2.5 w-2.5 text-white" /></div>
                              <span>SAV</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-gray-400 rounded-sm opacity-30"></div>
                              <span>Week-end/férié (non travaillé)</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-red-100 rounded-sm border"></div>
                              <span>Jour férié</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-orange-50 rounded-sm border"></div>
                              <span>Week-end</span>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Planning List View */}
        <TabsContent value="list">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Ordre des commandes</CardTitle>
              <p className="text-sm text-muted-foreground">
                Glissez-déposez pour réorganiser l'ordre des commandes
              </p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : planningData.planning_items.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune commande à planifier
                </p>
              ) : (
                <div className="space-y-2">
                  {planningData.planning_items.map((item, index) => (
                    <div
                      key={item.order_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`p-4 border rounded-sm flex items-center gap-4 cursor-move hover:bg-muted/50 transition-colors ${
                        item.alerts?.length > 0 ? "border-red-300 bg-red-50" : ""
                      }`}
                      data-testid={`planning-item-${item.order_id}`}
                    >
                      <GripVertical className="h-5 w-5 text-muted-foreground shrink-0" />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{item.reference}</span>
                          <Badge variant={item.status === "in_progress" ? "default" : "outline"}>
                            {item.status === "in_progress" ? "En cours" : "En attente"}
                          </Badge>
                          {item.allow_weekend && (
                            <Badge variant="secondary">Week-end OK</Badge>
                          )}
                        </div>
                        <p className="font-medium truncate">{item.client_name}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span>{formatCurrency(item.amount_ht)}</span>
                          <span>•</span>
                          <span>{item.duration_days} jours</span>
                          <span>•</span>
                          <span>{item.start_date} → {item.end_date}</span>
                        </div>
                        
                        {/* Alerts */}
                        {item.alerts?.map((alert, alertIdx) => (
                          <div key={alertIdx} className="flex items-center gap-2 mt-2 text-sm text-red-600">
                            <AlertTriangle className="h-4 w-4" />
                            {alert.message}
                          </div>
                        ))}
                        
                        {/* Constraints */}
                        {(item.start_constraint || item.end_constraint) && (
                          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                            {item.start_constraint && (
                              <span>Début souhaité: {item.start_constraint}</span>
                            )}
                            {item.end_constraint && (
                              <span>Fin limite: {item.end_constraint}</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openConstraintsDialog(item)}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Work Tracking */}
        <TabsContent value="tracking">
          <div data-testid="tab-content-tracking">
            <WorkTrackingTab />
          </div>
        </TabsContent>

        <TabsContent value="workload">
          <div data-testid="tab-content-workload">
            <WorkloadView />
          </div>
        </TabsContent>

        {/* Non-Working Days Settings */}
        <TabsContent value="settings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Jours non travaillés</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Gérez les jours fériés et les fermetures exceptionnelles
                </p>
              </div>
              <Button onClick={() => setNonWorkingDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un jour
              </Button>
            </CardHeader>
            <CardContent>
              {nonWorkingDays.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucun jour non travaillé configuré
                </p>
              ) : (
                <div className="space-y-2">
                  {nonWorkingDays.map((day) => (
                    <div
                      key={day.id}
                      className="flex items-center justify-between p-3 border rounded-sm"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-16">
                          <p className="text-lg font-bold">
                            {new Date(day.date + "T00:00:00").getDate()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {monthNames[new Date(day.date + "T00:00:00").getMonth()]}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium">{day.name}</p>
                          <div className="flex gap-2">
                            {day.recurring && (
                              <Badge variant="outline" className="text-xs">Récurrent</Badge>
                            )}
                            {day.is_default && (
                              <Badge variant="secondary" className="text-xs">Jour férié</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteNonWorkingDay(day.id)}
                        title={day.is_default ? "Marquer comme travaillé" : "Supprimer"}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Constraints Dialog */}
      <Dialog open={constraintsDialogOpen} onOpenChange={setConstraintsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Contraintes de planning</DialogTitle>
            <DialogDescription>
              Définissez les contraintes de dates pour cette commande
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-sm">
                <p className="font-mono text-sm">{selectedOrder.reference}</p>
                <p className="font-medium">{selectedOrder.client_name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(selectedOrder.amount_ht)} • {selectedOrder.duration_days} jours
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Date de début souhaitée</Label>
                <Input
                  type="date"
                  value={constraints.start_date_constraint}
                  onChange={(e) => setConstraints({ ...constraints, start_date_constraint: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date de fin limite</Label>
                <Input
                  type="date"
                  value={constraints.end_date_constraint}
                  onChange={(e) => setConstraints({ ...constraints, end_date_constraint: e.target.value })}
                />
              </div>
              
              <div className="space-y-3 p-3 border rounded-sm bg-muted/30">
                <Label className="text-sm font-medium">Jours de travail autorisés</Label>
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="allow-saturday"
                      checked={constraints.allow_saturday}
                      onCheckedChange={(checked) => setConstraints({ ...constraints, allow_saturday: checked })}
                    />
                    <Label htmlFor="allow-saturday" className="text-sm">Samedi</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="allow-sunday"
                      checked={constraints.allow_sunday}
                      onCheckedChange={(checked) => setConstraints({ ...constraints, allow_sunday: checked })}
                    />
                    <Label htmlFor="allow-sunday" className="text-sm">Dimanche</Label>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setConstraintsDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveConstraints}>
                  Enregistrer
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Non-Working Day Dialog */}
      <Dialog open={nonWorkingDialogOpen} onOpenChange={setNonWorkingDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un jour non travaillé</DialogTitle>
            <DialogDescription>
              Ajoutez un jour de fermeture, pont ou congé
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={newNonWorkingDay.date}
                onChange={(e) => setNewNonWorkingDay({ ...newNonWorkingDay, date: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Ex: Pont du 8 mai, Fermeture annuelle..."
                value={newNonWorkingDay.name}
                onChange={(e) => setNewNonWorkingDay({ ...newNonWorkingDay, name: e.target.value })}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="recurring"
                checked={newNonWorkingDay.recurring}
                onCheckedChange={(checked) => setNewNonWorkingDay({ ...newNonWorkingDay, recurring: checked })}
              />
              <Label htmlFor="recurring" className="text-sm">
                Récurrent chaque année (même jour/mois)
              </Label>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setNonWorkingDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddNonWorkingDay} disabled={!newNonWorkingDay.date || !newNonWorkingDay.name}>
                Ajouter
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* SAV Edit Dialog (extracted) */}
      <SavEditDialog
        open={savDialogOpen}
        onOpenChange={setSavDialogOpen}
        savForm={savForm}
        setSavForm={setSavForm}
        editingSav={editingSav}
        orders={orders}
        subcontractors={subcontractors}
        employees={employees}
        onSave={handleSaveSav}
        onDelete={handleDeleteSav}
        onToggleEmployee={toggleSavEmployee}
      />
    </div>
  );
};

export default CalendarPage;
